// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  // [::1] is the IPv6 localhost address.
  window.location.hostname === '[::1]' ||
  // 127.0.0.0/8 are considered localhost for IPv4.
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

export function register(config) {
  // Allow registration in production or on localhost for local testing
  if ((process.env.NODE_ENV === 'production' || isLocalhost) && 'serviceWorker' in navigator) {
    try { console.debug('[SW] register() start', { env: process.env.NODE_ENV, isLocalhost, publicUrl: process.env.PUBLIC_URL }); } catch(_) {}
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Log detailed diagnostics instead of silent return to help detect homepage / deployment mismatches.
      console.warn('[SW] PUBLIC_URL origin mismatch; attempting to continue. This may prevent SW registration.', {
        publicUrl: publicUrl.href,
        publicOrigin: publicUrl.origin,
        locationOrigin: window.location.origin
      });
      // Heuristic: if hostname matches but protocol differs (http->https) allow continue.
      if (publicUrl.hostname !== window.location.hostname) {
        console.warn('[SW] Hostnames differ; aborting SW registration to avoid scope issues. Fix homepage setting.');
        return;
      }
    }

    const startRegistration = () => {
      try {
        const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
        console.debug('[SW] computed swUrl (early)', swUrl);

        // Dev-only: if not production and explicit flag set, register a tiny test SW from a blob.
        // Enable by running in console: localStorage.setItem('enableDevServiceWorker','true') then reload.
        const enableDevSW = (process.env.NODE_ENV !== 'production') && (localStorage && localStorage.getItem && localStorage.getItem('enableDevServiceWorker') === 'true');
        if (enableDevSW) {
          try {
            const devSwCode = `self.addEventListener('install', e=>self.skipWaiting());\nself.addEventListener('activate', e=>e.waitUntil(self.clients.claim()));\nself.addEventListener('push', e=>{let d={};try{d=e.data.json();}catch(err){try{d={body:e.data?e.data.text():''}}catch(e){} }console.log('[dev-blob-sw] push',d);e.waitUntil((async ()=>{try{const cs=await clients.matchAll({includeUncontrolled:true});for(const c of cs){try{c.postMessage({type:'PUSH',payload:d})}catch(ex){}}}catch(ex){}return self.registration.showNotification(d.title||'Dev Test',{body:d.body||'',icon:'/favicon-32x32.png'});} )());});\nself.addEventListener('notificationclick', e=>{e.notification.close();e.waitUntil(clients.openWindow('/'));});`;
            const blob = new Blob([devSwCode], { type: 'application/javascript' });
            const blobUrl = URL.createObjectURL(blob);
            navigator.serviceWorker.register(blobUrl).then(registration => {
              console.log('Registered dev blob service worker', registration);
            }).catch(err => {
              console.error('Failed to register dev blob SW', err);
            });
            return; // don't continue with normal registration
          } catch (err) {
            console.warn('Dev SW registration failed, falling back to normal SW registration', err);
          }
        }

  if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
            'worker. To learn more, visit https://cra.link/PWA'
          );
        });
        } else {
          // Is not localhost. Just register service worker
          console.debug('[SW] registering valid SW (non-localhost)');
          registerValidSW(swUrl, config);
        }
      } catch (err) {
        console.error('[SW] early registration failed', err);
      }
    };
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      startRegistration();
    } else {
      document.addEventListener('DOMContentLoaded', startRegistration, { once: true });
    }
  }
}

function registerValidSW(swUrl, config) {
  console.debug('[SW] registerValidSW called', swUrl);
  navigator.serviceWorker
    .register(swUrl, { updateViaCache: 'none' })
    .then((registration) => {
      console.debug('[SW] registration successful', registration.scope);
      // Proactively check for updates shortly after registration
      try { setTimeout(() => { try { registration.update(); } catch (_) {} }, 1500); } catch (_) {}
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log(
                'New content is available and will be used when all ' +
                'tabs for this page are closed. See https://cra.link/PWA.'
              );

              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              console.log('Content is cached for offline use.');

              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  console.debug('[SW] checkValidServiceWorker', swUrl);
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        console.warn('[SW] No valid SW script at URL; unregistering then reloading', swUrl, response.status, contentType);
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        console.debug('[SW] Valid SW script found; proceeding to registration');
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
